import React, { useMemo, useState } from 'react';
import { Button, Grid } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { Confirm, useNotify } from 'react-admin';
import AgGrid from '../../../components/ag-grid/ag-grid';
import { ActionColumn } from '../../../components/ag-grid/aggrid-components';
import MDialog from '../../../components/controls/MDialog';
import MTextField from '../../../components/controls/MTextField';
import { dataProvider } from '../../../DataProvider';

interface QueueUploadDescriptionProps {
    /**
     * Called whenever the descriptions list changes (add / edit / delete) so that
     * the parent can keep the {@link RejectedDescriptionDialog} dropdown in sync.
     * @param descriptions - Updated sorted list of description strings.
     */
    onDescriptionsChanged: (descriptions: string[]) => void;
}

interface RowData {
    /** Row index used as the ag-Grid row id. */
    id: number;
    /** Description text as stored in the database. */
    error: string;
}

interface GridData {
    /** The full master list used when syncing changes back to the parent. */
    forUpdate: string[];
    /** The subset currently displayed in the ag-Grid (may be filtered). */
    forGrid: string[];
}

/**
 * Component A — "Add / manage Queue Upload rejection descriptions".
 *
 * Placed in the `actions` slot of {@link RenderControls} on the Queue Upload page,
 * providing the same add / edit / delete workflow as `ProcessError` on the
 * Process Tracking Report page, but with two key differences:
 *
 * 1. **No `errorList` prop** — descriptions are loaded directly from the backend
 *    (`QueueUpload/GetDescriptions`) rather than being passed from the parent.
 * 2. **No market prefix** — descriptions are plain strings, not `"Market: Error"` pairs.
 *
 * On any successful change the `onDescriptionsChanged` callback is called so
 * that the parent's {@link RejectedDescriptionDialog} dropdown stays current.
 *
 * @param props - {@link QueueUploadDescriptionProps}
 *
 * @example
 * // In QueueUploadPage — passed as the `actions` prop of RenderControls:
 * <QueueUploadDescription onDescriptionsChanged={setDescriptions} />
 */
const QueueUploadDescription: React.FC<QueueUploadDescriptionProps> = ({ onDescriptionsChanged }) => {
    const notify = useNotify();

    const [popups, setPopups] = useState<{
        main: boolean;
        delete: boolean;
        edit: { open: boolean; params?: any };
    }>({ main: false, delete: false, edit: { open: false } });

    const [newError, setNewError] = useState<string>('');
    const [invalidInput, setInvalidInput] = useState<boolean>(false);
    const [originRow, setOriginRow] = useState<RowData | null>(null);
    const [gridData, setGridData] = useState<GridData>({ forUpdate: [], forGrid: [] });
    const [loaded, setLoaded] = useState<boolean>(false);

    // ---------------------------------------------------------------------------
    // Load from DB when the dialog opens for the first time (lazy load)
    // ---------------------------------------------------------------------------

    /**
     * Fetches the current description list from the backend and seeds the grid.
     * Called once the first time the user opens the "Add Description" dialog.
     */
    const loadDescriptions = () => {
        if (loaded) return;
        dataProvider.fetchData('QueueUpload', 'GetDescriptions')
            .then((res: any) => {
                const list: string[] = res?.data?.descriptions
                    ? Object.keys(res.data.descriptions).sort()
                    : [];
                setGridData({ forUpdate: list, forGrid: list });
                setLoaded(true);
            })
            .catch(() => notify('Failed to load descriptions.', { type: 'error' }));
    };

    const handleOpenMain = () => {
        loadDescriptions();
        setPopups(prev => ({ ...prev, main: true }));
    };

    // ---------------------------------------------------------------------------
    // Add
    // ---------------------------------------------------------------------------

    /**
     * Submits a new description to the backend (`AddDescription` endpoint).
     * Updates the local grid and notifies the parent on success.
     */
    const handleSave = () => {
        if (!newError.trim()) {
            notify('Please fill in the new description!', { type: 'error' });
            setInvalidInput(true);
            return;
        }

        dataProvider.postData('QueueUpload', 'AddDescription', { error: newError.trim(), category: 'QueueUpload' })
            .then((res: any) => {
                if (res?.data?.message === 'success') {
                    const updated = [...gridData.forUpdate, newError.trim()].sort();
                    setGridData({ forUpdate: updated, forGrid: updated });
                    onDescriptionsChanged(updated);
                    notify('Description added successfully.', { type: 'success' });
                    setNewError('');
                } else if (res?.data?.message === 'exist') {
                    notify('Description already exists.', { type: 'error' });
                } else {
                    notify('Failed to add description.', { type: 'error' });
                }
            })
            .catch((err: any) => notify(`Something went wrong: ${err}`, { type: 'error', autoHideDuration: 2000 }))
            .finally(() => setInvalidInput(false));
    };

    // ---------------------------------------------------------------------------
    // Edit / Delete
    // ---------------------------------------------------------------------------

    /**
     * Sends an edit or delete request to the backend (`EditDescription` endpoint).
     *
     * @param error - The new (edit) or current (delete) description text.
     * @param type - `'edit'` or `'delete'`.
     * @param originError - The original text being replaced (only required for `'edit'`).
     */
    const handleEdit = (error: string, type: 'edit' | 'delete', originError?: string) => {
        dataProvider.postData('QueueUpload', 'EditDescription', {
            error,
            type,
            originError: originError ?? error,
            category: 'QueueUpload',
        })
            .then((res: any) => {
                if (res?.data?.message === 'success') {
                    let updated: string[];
                    if (type === 'edit') {
                        updated = gridData.forUpdate
                            .map(x => x === originError ? error : x)
                            .sort();
                    } else {
                        updated = gridData.forUpdate.filter(x => x !== error).sort();
                    }
                    setGridData({ forUpdate: updated, forGrid: updated });
                    onDescriptionsChanged(updated);
                    notify(`${type === 'edit' ? 'Updated' : 'Deleted'} successfully.`, { type: 'success' });
                } else {
                    notify(`${type === 'edit' ? 'Update' : 'Delete'} failed.`, { type: 'error' });
                }
            })
            .catch((err: any) => notify(`Something went wrong: ${err}`, { type: 'error', autoHideDuration: 2000 }))
            .finally(() => setPopups(prev => ({ ...prev, delete: false, edit: { open: false } })));
    };

    // ---------------------------------------------------------------------------
    // ag-Grid wiring (mirrors ProcessError pattern)
    // ---------------------------------------------------------------------------

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        flex: 1,
        filter: false,
        suppressMovable: true,
    }), []);

    const colDefs = [
        {
            field: 'error',
            headerName: 'Description',
            editable: true,
            minWidth: 500,
        },
        {
            headerName: 'Action',
            minWidth: 150,
            cellRenderer: ActionColumn,
            colId: 'action',
        },
    ];

    const onCellClicked = (params: any) => {
        if (params.column.colId !== 'action' || !params.event.target.dataset.action) return;
        const action = params.event.target.dataset.action;

        if (action === 'edit') {
            params.api.startEditingCell({
                rowIndex: params.node.rowIndex,
                colKey: params.columnApi.getDisplayedCenterColumns()[0].colId,
            });
        }
        if (action === 'delete') {
            setOriginRow(params.data);
            setPopups(prev => ({ ...prev, delete: true }));
        }
        if (action === 'update') {
            params.api.stopEditing(false);
            params.api.stopEditing();
        }
        if (action === 'cancel') {
            params.api.stopEditing(true);
        }
    };

    const onCellEditingStopped = (params: any) => {
        params.api.refreshCells({ columns: ['action'], rowNodes: [params.node], force: true });
        if (params.valueChanged) {
            setPopups(prev => ({ ...prev, edit: { open: true, params } }));
        }
    };

    const onCellEditingStarted = (params: any) => {
        params.api.refreshCells({ columns: ['action'], rowNodes: [params.node], force: true });
    };

    const rowDataForGrid = gridData.forGrid.map((value, index) => ({ id: index, error: value }));

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    return (
        <>
            <Button onClick={handleOpenMain}>+ Add Description</Button>

            <MDialog
                maxWidth="lg"
                open={popups.main}
                handleClose={() => setPopups(prev => ({ ...prev, main: false }))}
                title="Manage Queue Upload Descriptions"
                name="queue-upload-description-dialog"
            >
                <Grid container style={{ height: '55vh' }}>
                    {/* Input row */}
                    <Grid item size={10}>
                        <MTextField
                            error={invalidInput}
                            label="Description"
                            placeholder="Input new description"
                            onChange={(value: string) => setNewError(value)}
                            required
                        />
                    </Grid>
                    <Grid item size={2}>
                        <Button
                            style={{ marginTop: '20px', marginLeft: '10px' }}
                            variant="contained"
                            color="info"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    </Grid>

                    {/* Grid */}
                    <Grid item size={12} sx={{ '.ag-cell': { 'line-height': '40px' } }}>
                        <AgGrid
                            columnDefs={colDefs}
                            defaultColDef={defaultColDef}
                            setDefaultValues={{ sortField: { field: 'id', order: 'asc' } }}
                            rowHeight={45}
                            rowSelection="single"
                            editType="fullRow"
                            onCellClicked={onCellClicked}
                            onCellEditingStopped={onCellEditingStopped}
                            onCellEditingStarted={onCellEditingStarted}
                            gridHeight="45vh"
                            hidePaging={true}
                            rowData={rowDataForGrid}
                        />
                    </Grid>
                </Grid>
            </MDialog>

            {/* Delete confirmation */}
            <Confirm
                isOpen={popups.delete}
                title="Delete Description?"
                content={`Description: "${originRow?.error}"`}
                onConfirm={() => originRow && handleEdit(originRow.error, 'delete')}
                onClose={() => setPopups(prev => ({ ...prev, delete: false }))}
            />

            {/* Edit confirmation */}
            <Confirm
                isOpen={popups.edit.open}
                title="Edit Description?"
                content={`Do you want to update "${popups.edit.params?.oldValue}" → "${popups.edit.params?.newValue}"?`}
                onConfirm={() =>
                    handleEdit(
                        popups.edit.params.data.error,
                        'edit',
                        popups.edit.params.oldValue,
                    )
                }
                onClose={() => {
                    // Revert the cell value to the original text
                    const node = popups.edit.params?.api?.getRowNode(String(popups.edit.params.data.id));
                    if (node) node.setDataValue('error', popups.edit.params.oldValue);
                    setPopups(prev => ({ ...prev, edit: { open: false } }));
                }}
            />
        </>
    );
};

export default QueueUploadDescription;
